// Home.jsx
import React, { useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  FaLaptopCode,
  FaUser,
  FaJava,
  FaJs,
  FaTypo3,
  FaPython,
  FaDatabase,
  FaLeaf,
} from "react-icons/fa";
import { Card, CardContent } from "../components/ui/Card";
import BlogSection from "./BlogSection";
import SEO from "../components/SEO";

const Home = () => {
  const snippetRef = useRef(null);
  const blogRef = useRef(null);
  const topRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location]);

  const codeSnippets = [
    {
      title: "Java",
      description: "Explore core concepts and examples in Java.",
      route: "/java-snippet",
      icon: <FaJava className="text-orange-600 text-3xl mb-2" />,
      comingSoon:false
    },
    {
      title: "JavaScript",
      description: "Master the DOM, events, and logic with JS.",
      route: "/snippets/javascript",
      icon: <FaJs className="text-yellow-500 text-3xl mb-2" />,
      comingSoon:true
    },
    {
      title: "TypeScript",
      description: "Add static typing to your JavaScript code.",
      route: "/snippets/typescript",
      icon: <FaTypo3 className="text-blue-500 text-3xl mb-2" />,
      comingSoon:true
    },
    {
      title: "Python",
      description: "Simple and powerful scripting language.",
      route: "/snippets/python",
      icon: <FaPython className="text-green-600 text-3xl mb-2" />,
      comingSoon:true
    },
    {
      title: "MySQL",
      description: "Query and manage your relational data.",
      route: "/snippets/mysql",
      icon: <FaDatabase className="text-indigo-600 text-3xl mb-2" />,
      comingSoon:true
    },
    {
      title: "Spring Boot",
      description: "Build production-ready Java apps quickly.",
      route: "/snippets/spring-boot",
      icon: <FaLeaf className="text-green-500 text-3xl mb-2" />,
      comingSoon:true
    },
  ];

  return (
    <div
      ref={topRef}
      className="min-h-screen bg-white text-black dark:bg-gray-900 dark:text-white"
      style={{ scrollBehavior: "smooth" }}
    >
      <SEO
        title="CodeRocker - Snippets You Can Run, Blogs You’ll Love"
        description="From hands-on coding to deep-dive articles, we make tech learning quick, practical, and actually enjoyable."
        keywords="code snippets, tech blogs, JavaScript code, Java code, programming, coderocker, interview, online compiler"
        url="https://coderocker.netlify.app"
      />
      
      {/* Welcome Section */}
      <section
        id="top"
        className="flex flex-col items-center justify-center py-10 sm:py-16 text-center px-4 sm:px-6"
        style={{ scrollMarginTop: "100px" }}
      >
        <div className="flex gap-4 mb-6 flex-wrap justify-center">
          <FaLaptopCode size={40} className="text-indigo-400 animate-bounce sm:size-50" />
          <FaUser size={40} className="text-pink-500 animate-bounce sm:size-50" />
        </div>
        <div className="flex flex-col items-center space-y-4 max-w-4xl mx-auto">
          <span className="font-extrabold text-4xl sm:text-6xl font-mono text-indigo-700 dark:text-indigo-400 leading-tight">
            CodeRocker
          </span>
          <span className="font-sans text-lg sm:text-2xl text-gray-900 dark:text-gray-100 leading-snug">
             Snippets You Can Run, Blogs You’ll Love.
          </span>
          <span className="text-base sm:text-lg text-gray-700 dark:text-gray-300 leading-relaxed px-2 sm:px-0">
            From hands-on coding to deep-dive articles, we make tech learning quick,
            practical, and actually enjoyable.
          </span>
        </div>
      </section>

      {/* Code Snippets Section */}
      <section
        id="code-snippets"
        ref={snippetRef}
        className="flex flex-col items-center py-8 sm:py-12"
        style={{ scrollMarginTop: "100px" }}
      >
        <h2 className="text-2xl sm:text-3xl font-semibold mb-8">Code Snippets</h2>
        <p className="sr-only">
          code snippets, Java code, programming, coderocker, interview, java online compiler, practice java online, Java Snippets
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-10 px-4 w-full max-w-6xl">
          {codeSnippets.map((snippet, index) => {
            const cardContent = (
              <Card className="p-6 shadow-lg dark:shadow-purple-800">
                <CardContent
                  className={`flex flex-col items-center text-center ${
                    snippet.comingSoon ? "opacity-40" : "opacity-100"
                  }`}
                >
                  {snippet.icon}
                  <h3 className="text-lg sm:text-xl font-bold mb-2">{snippet.title}</h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {snippet.description}
                  </p>
                </CardContent>
                {snippet.comingSoon && (
                  <div className="absolute top-2 right-[-20px] rotate-45 bg-red-600 text-white text-xs px-6 py-1 font-bold shadow-lg">
                    Coming Soon
                  </div>
                )}
              </Card>
            );

            return (
              <div key={index} className="relative w-full">
                {snippet.comingSoon ? (
                  cardContent
                ) : (
                  <Link to={snippet.route} className="no-underline w-full">
                    {cardContent}
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Blogs Section */}
      <section
        id="blogs"
        ref={blogRef}
        className="flex flex-col items-center py-8 sm:py-12 w-full px-4"
      >
        <p className="sr-only">
          tech blogs, programming articles, coding tutorials, java, javascript, react 
        </p>
        <BlogSection />
      </section>
    </div>
  );
};

export default Home;
